import { Dialog, RadioGroup, Transition } from '@headlessui/react';
import { EnvelopeIcon, PhoneIcon, PrinterIcon } from '@heroicons/react/24/solid';
import { isValidPhoneNumber } from 'libphonenumber-js';
import ms from 'ms';
import Image from 'next/image';
import Link from 'next/link';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { InlineShareButtons } from 'sharethis-reactjs';
import HtmlContentContainer from '@/components/html-content-container';
import ProductDetail from '@/components/product-detail';
import classNames from '@/lib/class-names';
import sizes4sort from '@/lib/integrate-parameter-data-sizes';
import productImageLoader from '@/lib/product-image-loader';
import trpc from '@/lib/trpc';
import type { RouterOutput } from '@/lib/trpc';
import type { Dispatch, SetStateAction } from 'react';

type InquiryRequestFormData = {
  note: string;
  personEmailAddress: string;
  personName: string;
  personPhoneNumber: string;
  productCode: string;
  productHref: string;
  productName: string;
};

type ProductDetail = NonNullable<RouterOutput['product']['detail']>;

// *****************************************
// *
// * ContentProductDetailPage
// *
// *****************************************

type ContentProductDetailPageProps = {
  className?: string;
  productHref: string;
} & ProductDetail;

type Media = {
  alt: string;
  height: number | null;
  src: string;
  width: number | null;
};

type Selected = {
  color: string | null;
  disponibleSizes: string[];
  images: Media[];
  size: string | null;
  sku: string | null;
};

function getVariantImage(media: Media[], variantImage: string | null): Media {
  return (
    media.find((m) => {
      const variantWithouExtension = variantImage?.replace(/\.[^/.]+$/, '');
      const mediaWithoutExtension = m.src.replace(/\.[^/.]+$/, '');
      return variantWithouExtension === mediaWithoutExtension;
    }) || media[0]
  );
}

export default function ContentProductDetailPage({
  className,
  productHref,
  ...productDetail
}: ContentProductDetailPageProps) {
  const [isInquiryDialogOpen, setIsInquiryDialogOpen] = useState<boolean>(false);
  const s4o = sizes4sort.reduce((acc, v) => {
    acc.set(v.code, v.ordinal);
    return acc;
  }, new Map<string, number>());

  const [selected, updateSelected] = useState<Selected>({
    color: null,
    disponibleSizes: [],
    images: [productDetail.media[0] || { alt: '', height: null, src: '', width: null }],
    size: null,
    sku: productDetail.sku,
  });

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    const colorIds = productDetail.colors.map((c) => c.id);
    const sizeIds = (productDetail.sizes || []).map((size) => size.valueForCode);
    const variants = productDetail.productVariants;

    if ((colorIds.length != 0 || sizeIds.length != 0) && variants.length > 0) {
      const firstColorId = colorIds.find((c) => variants.find((i) => i.color === c));

      if (firstColorId != null) {
        // variantsWithColor: always at least 1 variant
        const variantsWithColor = variants.filter((i) => i.color === firstColorId);
        const disponibleSizes = variantsWithColor.map((v) => v.size).filter((i): i is string => !!i);
        const firstSize = sizeIds.find((s) => disponibleSizes.includes(s));
        // finalVariant: colored variants without sizes could be found
        const finalVariant =
          firstSize != null ? variantsWithColor.find((v) => v.size === firstSize) : variantsWithColor[0];
        if (finalVariant != null) {
          return updateSelected({
            ...finalVariant,
            disponibleSizes,
            images: [getVariantImage(productDetail.media, finalVariant.image)],
          });
        }
      }

      const firstSizeId = sizeIds.find((s) => variants.find((i) => i.size === s));
      const firstSizeVariant = firstSizeId != null ? variants.find((i) => i.size === firstSizeId) : variants[0];
      // always 1 variant the condition is due to ts
      if (firstSizeVariant != null) {
        return updateSelected({
          ...firstSizeVariant,
          disponibleSizes: sizeIds,
          images: [getVariantImage(productDetail.media, firstSizeVariant.image)],
        });
      }
    }

    // For solo product
    updateSelected({
      color: null,
      disponibleSizes: [],
      images: [productDetail.media[0] || { alt: '', height: null, src: '', width: null }],
      size: null,
      sku: productDetail.sku,
    });
  }, [
    productDetail.colors,
    productDetail.media,
    productDetail.productVariants,
    productDetail.sizes,
    productDetail.sku,
  ]);

  const updateSelectedColor = useCallback(
    (colorId: string) => {
      const variantsWithColor = productDetail.productVariants.filter((p) => p.color === colorId);
      if (variantsWithColor.length > 0) {
        const disponibleSizes = variantsWithColor.map((v) => v.size).filter((i): i is string => !!i);
        const includesSelfSize = variantsWithColor.findIndex((p) => p.size === selected.size);
        if (includesSelfSize >= 0) {
          const finalVariant = variantsWithColor[includesSelfSize];
          return updateSelected({
            ...finalVariant,
            disponibleSizes,
            images: [getVariantImage(productDetail.media, finalVariant.image)],
          });
        }

        const firstSize = productDetail.sizes.find((s) => disponibleSizes.includes(s.valueForCode));

        const finalVariant = firstSize
          ? variantsWithColor.find((v) => v.size === firstSize.valueForCode)
          : variantsWithColor[0];

        if (finalVariant != null) {
          return updateSelected({
            ...finalVariant,
            disponibleSizes,
            images: [getVariantImage(productDetail.media, finalVariant.image)],
          });
        }
      } else {
        console.error('No variants under selected color');
      }
    },
    [productDetail.media, productDetail.productVariants, productDetail.sizes, selected.size],
  );

  const updateSelectedSize = useCallback(
    (sizeId: string) => {
      const sizeCode = productDetail.sizes.find((size) => size.displayValue === sizeId)?.valueForCode || sizeId;
      const variants = productDetail.productVariants.filter((p) => p.size === sizeCode);
      if (variants.length > 0) {
        const newProductVariant = selected.color ? variants.find((p) => p.color === selected.color) : variants[0];

        if (newProductVariant != null) {
          updateSelected((prevState) => {
            return {
              ...newProductVariant,
              disponibleSizes: prevState.disponibleSizes,
              images: [getVariantImage(productDetail.media, newProductVariant.image)],
            };
          });
        }
      } else {
        console.error('No variants under selected size.');
      }
    },
    [productDetail.media, productDetail.productVariants, productDetail.sizes, selected.color],
  );

  return (
    <>
      <ProductDetail className='mb-8 print:flex print:w-full print:flex-col lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-8'>
        {/* Product name and primary image for print only */}
        <div className='hidden print:block'>
          <ProductDetail.Name className='hidden print:mt-8 print:block print:text-3xl print:font-bold print:tracking-tight'>
            {productDetail.name}
          </ProductDetail.Name>
          {productDetail.media != null && productDetail.media.length > 0 && (
            <div className='mt-8'>
              <div className='aspect-1 relative mx-auto w-[60%]'>
                <Image
                  alt={productDetail.media[0].alt ?? ''}
                  className='h-full w-full rounded-lg object-contain object-center'
                  fill={!(productDetail.media[0].height && productDetail.media[0].width)}
                  height={
                    productDetail.media[0].width && productDetail.media[0].height
                      ? productDetail.media[0].height
                      : undefined
                  }
                  loader={productImageLoader}
                  src={productDetail.media[0].src}
                  unoptimized
                  width={
                    productDetail.media[0].height && productDetail.media[0].width
                      ? productDetail.media[0].width
                      : undefined
                  }
                />
              </div>
            </div>
          )}
        </div>
        <ProductDetail.Gallery
          className='print:hidden'
          productMedia={selected.images}
          productName={productDetail.name}
        />
        <div className='mt-10 px-4 sm:mt-16 sm:px-0 lg:mt-0'>
          {/* Product name for screen only */}
          <ProductDetail.Name className='text-3xl font-bold tracking-tight print:hidden'>
            {productDetail.name}
          </ProductDetail.Name>
          <ProductDetail.Price
            className='mt-3'
            currency={productDetail.currency}
            innerWrapperClassName='text-2xl tracking-tight print:font-bold'
            priceList={productDetail.priceList}
            priceListWithTax={productDetail.priceListWithTax}
          />
          {/* <ProductDetail.Reviews rating={3} /> */}
          {productDetail.description != null && (
            <ProductDetail.Description className='mt-6'>
              <HtmlContentContainer className='prose space-y-6' html={productDetail.description} />
            </ProductDetail.Description>
          )}

          <ProductDetail.Variants className='mt-6'>
            {productDetail.colors.length > 0 && (
              <div>
                <h3 className='text-content-suspend text-sm'>Barvy</h3>
                <RadioGroup
                  className='mt-2'
                  onChange={updateSelectedColor}
                  value={selected.color || productDetail.colors[0].id}
                >
                  <RadioGroup.Label className='sr-only'>{'Vyberte barvu'}</RadioGroup.Label>
                  <span className='flex flex-wrap items-center'>
                    {productDetail.colors.map((color) => (
                      <RadioGroup.Option
                        key={color.name}
                        className={({ active, checked }) =>
                          classNames(
                            color.color,
                            active && checked ? 'ring ring-offset-1' : '',
                            !active && checked ? 'ring-2' : '',
                            // 'cursor-pointer',
                            'relative -ml-0.5 -mt-0.5 mr-3 mb-3 flex items-center justify-center rounded-full p-0.5 focus:outline-none',
                          )
                        }
                        title={color.name}
                        value={color.id}
                      >
                        <RadioGroup.Label as='span' className='sr-only'>
                          {color.name}
                        </RadioGroup.Label>
                        <span
                          aria-hidden='true'
                          className={'h-8 w-8 rounded-full border border-black/10'}
                          style={{ backgroundColor: color.color }}
                        />
                      </RadioGroup.Option>
                    ))}
                  </span>
                </RadioGroup>
              </div>
            )}
            {productDetail.sizes.length > 0 && (
              <>
                {/* Size picker */}
                <div className='mt-8'>
                  <div className='flex items-center justify-between'>
                    <h3 className='text-content-suspend text-sm'>Velikosti</h3>
                    <Link
                      className='text-content-suspend hover:text-content-focus text-sm font-medium print:hidden'
                      href='/stranky/velikostni-tabulky'
                      prefetch={false}
                    >
                      Velikostní tabulky
                    </Link>
                  </div>

                  <RadioGroup className='mt-2' onChange={updateSelectedSize} value={selected.size}>
                    <RadioGroup.Label className='sr-only'>Výběr velikostí</RadioGroup.Label>
                    <div className='grid grid-cols-3 gap-3 print:grid-cols-8 sm:grid-cols-6'>
                      {productDetail.sizes
                        .sort((s1, s2) => (s4o.get(s1.valueForCode) ?? 0) - (s4o.get(s2.valueForCode) ?? 0))
                        .map((size) => (
                          <RadioGroup.Option
                            key={size.displayValue}
                            className={({ active, checked }) => {
                              return classNames(
                                selected.disponibleSizes.includes(size.valueForCode)
                                  ? 'cursor-pointer focus:outline-none'
                                  : 'cursor-not-allowed opacity-25',
                                active ? 'ring-2 ring-indigo-500 ring-offset-2' : '',
                                'border-surface-divid text-content bg-surface',
                                'flex items-center justify-center rounded-md border py-3 px-3 text-sm font-medium uppercase sm:flex-1',
                                checked ? 'ring-2 ring-indigo-500 ring-offset-2' : '',
                              );
                            }}
                            disabled={selected.disponibleSizes.includes(size.valueForCode) === false}
                            value={size.displayValue}
                          >
                            <RadioGroup.Label as='span'>{size.displayValue}</RadioGroup.Label>
                          </RadioGroup.Option>
                        ))}
                    </div>
                  </RadioGroup>
                </div>
              </>
            )}
          </ProductDetail.Variants>

          {selected.sku != null ? (
            <div className='sm:flex-col1 text-content mt-10 flex text-sm print:hidden'>
              {'Kód varianty: '}
              {selected.sku}
            </div>
          ) : (
            ''
          )}

          <div className='sm:flex-col1 mt-10 flex print:hidden'>
            <button className='btn btn-secondary' onClick={() => setIsInquiryDialogOpen(true)} type='button'>
              {'Získat informace'}
            </button>
          </div>

          <div className='sm:flex-col1 mt-10 flex print:hidden'>
            <div className='flex-1'>
              {typeof window === 'undefined' ? (
                <div />
              ) : (
                <InlineShareButtons
                  config={{
                    alignment: 'left',
                    color: 'social',
                    description: 'Sdílení produktu z katalogu Blyth',
                    enabled: true,
                    font_size: 16,
                    labels: null,
                    language: 'en',
                    message: `Dobrý den,\n\nOdesílatel s Vámi chce sdílet product z webu Blyth.cz, který lze nalézt na adrese níže:\n${productHref}\n\nS přáním hezkého dne,\nVáš tým Blyth`,
                    // the outlook and gmail are not in SharingNetwork type but they are required
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    networks: ['email', 'outlook', 'gmail', 'whatsapp', 'sharethis'],
                    padding: 16,
                    radius: 4,
                    show_total: false,
                    size: 40,
                    subject: 'Blyth katalog - sdílení produktu',
                    title: 'Blyth katalog - sdílení produktu',
                    username: 'custom twitter handle',
                  }}
                />
              )}
            </div>
            <div className='flex items-center'>
              <button aria-label='Tisk' onClick={() => window.print()} type='button'>
                <PrinterIcon aria-hidden='true' className='mx-2 h-6 w-6' />
              </button>
            </div>

            {/* <button
                className='ml-4 flex items-center justify-center rounded-md p-3 text-gray-400 hover:bg-gray-100 hover:text-gray-500'
                type='button'
              >
                <HeartIcon aria-hidden='true' className='h-6 w-6 shrink-0' />
                <span className='sr-only'>Add to favorites</span>
              </button> */}
          </div>

          {/* Spagetti for screen only. */}
          <ProductDetail.Spagetti className='mt-12 print:hidden'>
            {productDetail.descriptionLong && (
              <ProductDetail.Spagetti.Panel defaultOpen heading='Popis'>
                <HtmlContentContainer html={productDetail.descriptionLong} />
              </ProductDetail.Spagetti.Panel>
            )}
            {productDetail.parameters.length > 0 && (
              <ProductDetail.Spagetti.Panel
                heading='Parametry'
                panelProps={{
                  className: 'border-t border-surface-divid px-4 py-5 sm:p-0 bg-surface shadow sm:rounded-lg',
                }}
              >
                <ContentProductDetailPageParametersDl parameters={productDetail.parameters} />
              </ProductDetail.Spagetti.Panel>
            )}
          </ProductDetail.Spagetti>

          {/* Spagetti for print only. */}
          <div className='hidden print:block' style={{ pageBreakBefore: 'always' }}>
            {productDetail.descriptionLong && (
              <div>
                <h2 className='text-primary-suspend text-lg font-bold'>Popis</h2>
                <HtmlContentContainer html={productDetail.descriptionLong} />
              </div>
            )}
            {productDetail.parameters.length > 0 && (
              <div className='mt-8'>
                <h2 className='text-primary-suspend text-lg font-bold'>Parametry</h2>
                <ContentProductDetailPageParametersDl parameters={productDetail.parameters} />
              </div>
            )}
          </div>
        </div>
      </ProductDetail>
      <InquiryRequestDialog
        isInquiryDialogOpen={isInquiryDialogOpen}
        productCode={selected.sku || `[productCode]`}
        productHref={productHref}
        productName={productDetail.name}
        setIsInquiryDialogOpen={setIsInquiryDialogOpen}
      />
    </>
  );
}

function ContentProductDetailPageParametersDl({
  parameters,
}: {
  parameters: ContentProductDetailPageProps['parameters'];
}) {
  return (
    <dl className='sm:divide-surface-divid sm:divide-y'>
      {parameters
        .reduce((a, param) => {
          const idx = a.findIndex((x: any) => x.parameterCode === param.parameterCode);
          if (idx === -1) {
            const { displayValue, displayValueColor, label, parameterCode } = param;
            return [...a, { label, parameterCode, values: [{ displayValue, displayValueColor }] }];
          }
          const next: any = [...a];
          next[idx].values.push({
            displayValue: param.displayValue,
            displayValueColor: param.displayValueColor,
          });
          return next;
        }, [] as any)
        .map(
          (param: {
            label: string;
            parameterCode: string;
            values: { displayValue: string; displayValueColor: string }[];
          }) => (
            <div key={param.parameterCode} className='py-4 print:w-1/2 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6'>
              <dt className='text-content text-sm font-medium print:text-base print:underline'>{param.label}</dt>
              <dd className='text-content-focus mt-1 grid grid-cols-2 gap-1 text-sm sm:col-span-2 sm:mt-0'>
                {param.values.map((value, idx) => (
                  <span key={`parameter-${param.parameterCode}-${idx}`} className='inline-flex'>
                    {value.displayValueColor != null && (
                      <span aria-hidden='true' className='mr-2 h-4 w-4'>
                        {/* Wrapping ensures that inner span will be circle even for two line labels */}
                        <span
                          aria-hidden='true'
                          className={'inline-block h-4 w-4 rounded-full border border-black/10'}
                          style={{ backgroundColor: value.displayValueColor }}
                        />
                      </span>
                    )}
                    {value.displayValue}
                  </span>
                ))}
              </dd>
            </div>
          ),
        )}
    </dl>
  );
}

// *****************************************
// *
// * InquiryRequestDialog
// *
// *****************************************

function InquiryRequestDialog({
  isInquiryDialogOpen,
  productCode,
  productHref,
  productName,
  setIsInquiryDialogOpen,
}: {
  isInquiryDialogOpen: boolean;
  productCode: string;
  productHref: string;
  productName: string;
  setIsInquiryDialogOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const mutation = trpc.inquiryForm.useMutation();

  const [showThank, setShowThank] = useState<boolean>(false);
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined = undefined;
    if (showThank === true) {
      timer = setTimeout(() => {
        setShowThank(false);
      }, ms('30s'));
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [showThank]);

  const saveData = useCallback(
    (formData: InquiryRequestFormData): Promise<any> => {
      return mutation.mutateAsync(
        {
          note: formData.note,
          personEmailAddress: formData.personEmailAddress,
          personName: formData.personName,
          personPhoneNumber: formData.personPhoneNumber,
          productCode: productCode,
          productHref: productHref,
          productName: productName,
        },
        {
          onSuccess() {
            setShowThank(true);
          },
        },
      );
    },
    [mutation, productHref, productName, productCode],
  );

  return (
    <Transition.Root as={Fragment} show={isInquiryDialogOpen}>
      <Dialog as='div' className='relative z-10' onClose={setIsInquiryDialogOpen}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='bg-surface-focus/75 fixed inset-0 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-10 overflow-y-auto'>
          <div className='flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <Dialog.Panel className='bg-surface relative overflow-hidden rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6'>
                <Dialog.Title as='h3' className='text-content text-lg font-medium leading-6'>
                  {showThank ? (
                    'Vaše poptávka byla odeslána do naší společnosti'
                  ) : (
                    <>
                      {'Poptávkový formulář pro produkt '}
                      <span className='text-content-focus font-semibold'>{productName}</span>
                      <div className='text-content font-small'>{productCode}</div>
                    </>
                  )}
                </Dialog.Title>
                {showThank === true && (
                  <InquiryRequestDialogContentThankForSubmition closeDialog={() => setIsInquiryDialogOpen(false)} />
                )}
                {showThank === false && (
                  <InquiryRequestDialogContentForm
                    closeDialog={() => setIsInquiryDialogOpen(false)}
                    handleSave={saveData}
                  />
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

// *****************************************
// *
// * InquiryRequestDialogContentForm
// *
// *****************************************

function InquiryRequestDialogContentForm({
  closeDialog,
  handleSave,
}: {
  closeDialog: () => void;
  handleSave: (data: InquiryRequestFormData) => Promise<any>;
}) {
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<InquiryRequestFormData>();

  return (
    <form className='mt-6 flex flex-col' onSubmit={handleSubmit(handleSave)}>
      <div className='form-styles grid flex-1 grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8'>
        <div className='sm:col-span-2'>
          <div>
            <label htmlFor='personName'>
              {'Jméno Příjmení'}
              <abbr>*</abbr>
            </label>
            <div data-role='input'>
              <input
                autoComplete='name'
                id='personName'
                placeholder='Jan Novotný'
                type='text'
                {...register('personName', {
                  required: true,
                })}
                aria-errormessage={errors?.personName?.type === 'required' ? 'personName-err-required' : undefined}
                aria-invalid={errors?.personName ? 'true' : 'false'}
              />
            </div>
            {errors.personName?.type === 'required' && (
              <p id={'personName-err-required'} role='alert'>
                {'Pole je povinné.'}
              </p>
            )}
          </div>
        </div>
        <div>
          <label htmlFor='personEmailAddress'>
            {'Email'}
            <abbr>*</abbr>
          </label>
          <div className='relative' data-role='input-group'>
            <div data-role='adornment-start'>
              <EnvelopeIcon aria-hidden='true' className='h-5 w-5 text-gray-400' />
            </div>
            <input
              autoComplete='email'
              className='!pl-10'
              id='personEmailAddress'
              placeholder='vy@priklad.cz'
              type='email'
              {...register('personEmailAddress', {
                pattern:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\-\d]+\.)+[a-zA-Z]{2,}))$/,
                required: true,
              })}
              aria-errormessage={
                errors?.personEmailAddress?.type === 'required'
                  ? 'personEmailAddress-err-required'
                  : errors?.personEmailAddress?.type === 'pattern'
                    ? 'personEmailAddress-err-format'
                    : undefined
              }
              aria-invalid={errors?.personEmailAddress ? 'true' : 'false'}
            />
          </div>
          {errors.personEmailAddress?.type === 'required' && (
            <p id={'personEmailAddress-err-required'} role='alert'>
              {'Pole je povinné.'}
            </p>
          )}
          {errors.personEmailAddress?.type === 'pattern' && (
            <p id={'personEmailAddress-err-format'} role='alert'>
              {'Neplatný formát email adresy.'}
            </p>
          )}
        </div>
        <div>
          <label htmlFor='personPhoneNumber'>
            {'Telefon'}
            <abbr>*</abbr>
          </label>
          <div className='relative' data-role='input-group'>
            <div data-role='adornment-start'>
              <PhoneIcon aria-hidden='true' className='h-5 w-5' />
            </div>
            <input
              autoComplete='tel'
              className='!pl-10'
              id='personPhoneNumber'
              placeholder='+420123456789'
              type='text'
              {...register('personPhoneNumber', {
                validate: {
                  phoneNumber: (v: string | null | undefined) => isValidPhoneNumber(v || ''),
                  required: (v: string | null | undefined) => !!v,
                },
              })}
              aria-errormessage={
                errors?.personPhoneNumber?.type === 'required'
                  ? 'personPhoneNumber-err-required'
                  : errors?.personPhoneNumber?.type === 'phoneNumber'
                    ? 'personPhoneNumber-err-format'
                    : undefined
              }
              aria-invalid={errors?.personPhoneNumber ? 'true' : 'false'}
            />
          </div>
          {errors.personPhoneNumber?.type === 'required' && (
            <p id={'personPhoneNumber-err-required'} role='alert'>
              {'Pole je povinné.'}
            </p>
          )}
          {errors.personPhoneNumber?.type === 'phoneNumber' && (
            <p id={'personPhoneNumber-err-format'} role='alert'>
              {'Neplatné telefonní číslo.'}
            </p>
          )}
        </div>
        <div className='sm:col-span-2'>
          <div>
            <label htmlFor='note'>{'Poznámka'}</label>
            <div data-role='input'>
              <textarea
                autoComplete='off'
                id='note'
                placeholder=''
                {...register('note', {
                  required: true,
                })}
                aria-errormessage={errors?.note?.type === 'required' ? 'note-err-required' : undefined}
                aria-invalid={errors?.note ? 'true' : 'false'}
                rows={4}
              />
            </div>
            {errors.note?.type === 'required' && (
              <p id={'note-err-required'} role='alert'>
                {'Pole je povinné.'}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className='mt-5 flex items-center justify-end gap-3 sm:mt-6'>
        <button className='btn btn-primary' type='submit'>
          {'Odeslat'}
        </button>
        <button className='btn btn-outline' onClick={closeDialog} type='button'>
          {'Zavřít'}
        </button>
      </div>
    </form>
  );
}

// *****************************************
// *
// * InquiryRequestDialogContentThankForSubmition
// *
// *****************************************

function InquiryRequestDialogContentThankForSubmition({ closeDialog }: { closeDialog: () => void }) {
  return (
    <div className='mt-6 flex flex-col'>
      <div className='flex-1'>
        <p>
          <span className='font-medium'>{'Děkujeme za zájem o náš produkt'}</span>
          {', naše obchodní oddělení Vás bude obratem kontaktovat.'}
        </p>
      </div>
      <div className='mt-5 flex items-center justify-end gap-3 sm:mt-6'>
        <button className='btn btn-outline' onClick={closeDialog} type='button'>
          {'Zavřít'}
        </button>
      </div>
    </div>
  );
}
